import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import ErrorBoundary from 'components/ErrorBoundary';
import LogRocketInitializer from 'components/LogRocketInitializer';
import SentryInitializer from 'components/SentryInitializer';
import { SupportTicketChat } from 'components/SupportTicketChat';
import { SupportTicketWidget } from 'components/SupportTicketWidget';
import { TicketDetailModal } from 'components/TicketDetailModal';
import FeatureFlagInitializer from 'components/FeatureFlagInitializer';
import PublicFeatureFlagInitializer from 'components/PublicFeatureFlagInitializer';
import WebSocketInitializer from 'components/WebSocketInitializer';
import { StoreProvider } from 'contexts/StoreProvider';
import { observer } from 'mobx-react';
import { PrimeReactProvider } from 'primereact/api';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css'; //theme
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { rootStore } from 'stores/RootStore';
import AppRoutes from 'validations/Navigations';
import './App.css';
import config from './config';

const oktaAuth = new OktaAuth(config.oidc);

function App() {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth: any, originalUri: any) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    <>
      <ErrorBoundary>
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <StoreProvider store={rootStore}>
            <PrimeReactProvider>
              {/* Initialize LogRocket early in the component tree */}
              <LogRocketInitializer />

              {/* Initialize Sentry logging/debug hooks */}
              <SentryInitializer />

              {/* Initialize WebSocket connection when authenticated */}
              <WebSocketInitializer />

              {/* Load feature flags after authentication */}
              <FeatureFlagInitializer />
              <PublicFeatureFlagInitializer />

              <AppRoutes />

              {/* Support Ticket System - Only for authenticated users */}
              {rootStore.UserStore.isAuthenticated && (
                <>
                  <SupportTicketWidget />
                  <SupportTicketChat />
                  <TicketDetailModal />
                </>
              )}

              <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </PrimeReactProvider>
          </StoreProvider>
        </Security>
      </ErrorBoundary>
    </>
  );
}

export default observer(App);
